import { useStaticQuery, graphql } from "gatsby";
import React, { useMemo, useReducer } from "react";
import { has } from "lodash";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import Downshift from "downshift";
import queryString from "query-string";

import AutocompleteInput from "~/components/AutocompleteInput";
import AutocompleteResults from "~/components/AutocompleteResults";
import { SrOnlyLabel } from "~/styles/screenReaderText";

import { useDebouncedCallback } from "use-debounce";
import { useLocationContext } from "~/context/LocationContext";
import useBuildSearchResults from "~/hooks/useBuildSearchResults";
import { useTranslationContext } from "~/context/TranslationContext";

import { fetchInline } from "~/utilities/fetchHelper";
import { isBrowser } from "~/utilities/helpers";

import {
    initialState,
    downshiftReducer,
    reducer,
} from "~/reducers/autocomplete";

import { caption } from "~/styles/typographyStyles";

const handleModalCSS = ({ isModal, theme }) => {
    let styles = ``;

    if (isModal) {
        styles = `
            background-color: white;
            padding: 2rem 0 6rem 0;
            border-radius: 2px;
            min-height: 100vh;

            @media (min-width: ${theme.breakpoints.tabletPortrait}) {
                min-height: auto;
            }
        `;
    }

    return styles;
};

const Wrapper = styled.div`
    position: relative;
    margin-bottom: 40px;
    ${handleModalCSS};
`;

const Label = styled.label`
    ${caption};
    display: block;
    margin-bottom: 10px;
`;

const LabelWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 1rem;
`;

const getQueryParam = (paramName, location) => {
    let param = "";
    if (isBrowser) {
        let queryParams = queryString.parse(location.search);
        if (paramName && has(queryParams, paramName)) {
            param = queryParams[paramName];
        }
    }
    return param;
};

const getOptions = (value, isDocumentLibrary, activeCode) => {
    const values = {
        query: value,
        isDocumentLibrary,
        size: 30,
        active_code: activeCode,
    };
    return {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        mode: "cors",
        body: JSON.stringify(values),
    };
};

const fetchSuggestions = (value, isDocumentLibrary, url, activeCode) => {
    const options = getOptions(value, isDocumentLibrary, activeCode);
    return fetchInline(url, options)
        .then((data) => {
            const hits = data?.hits?.hits || [];
            return { hits };
        })
        .catch((error) => {
            console.error(error);
            return { error };
        });
};

const Autocomplete = ({
    updateParent = () => {},
    id,
    paramName = "",
    label,
    srOnly = false,
    isDocumentLibrary = false,
    placeholder = "",
    isModal = false,
    closeModal = () => {},
    resetParentState = null,
}) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { buildAutocompleteTable } = useBuildSearchResults();

    const {
        wp: {
            optionsPage: { globalOptions },
        },
    } = useStaticQuery(graphql`
        query StaticAutocompleteQuery {
            wp {
                optionsPage {
                    globalOptions {
                        autocompleteSearch
                    }
                }
            }
        }
    `);

    const { location } = useLocationContext();
    const { activeCode } = useTranslationContext();

    const query = useMemo(
        () => getQueryParam(paramName, location),
        [paramName, location],
    );

    const autocompleteEndpoint = globalOptions?.autocompleteSearch || "";
    const AUTOCOMPLETE_URL = `${process.env.GATSBY_ADMIN_SITE_URL}${autocompleteEndpoint}`;

    const debounce = useDebouncedCallback((value) => {
        fetchSuggestions(
            value,
            isDocumentLibrary,
            AUTOCOMPLETE_URL,
            activeCode,
        ).then((response) => {
            if (has(response, "error")) {
                dispatch({ type: "HANDLE_ERROR" });
            } else {
                const suggestions = buildAutocompleteTable(response.hits);
                dispatch({ type: "SET_SUGGESTIONS", suggestions });
            }
        });
    }, 1000);

    const handleInputChange = (changes) => {
        debounce.cancel();
        dispatch({ type: "START_LOADING" });
        if (has(changes, "inputValue") && changes.inputValue) {
            debounce(changes.inputValue);
        } else {
            dispatch({ type: "CLEAR_STATE" });
        }
    };

    return (
        <Downshift
            stateReducer={downshiftReducer}
            initialInputValue={query ?? ""}
            initialSelectedItem={{ title: query ?? "" }}
            itemToString={(item) => (item ? item.title : "")}
            onStateChange={(changes) => {
                if (Downshift.stateChangeTypes.changeInput === changes.type) {
                    handleInputChange(changes);
                }

                if (
                    Downshift.stateChangeTypes.keyDownEscape === changes.type &&
                    isModal
                ) {
                    closeModal();
                }
            }}
            onChange={(value) => {
                if (value) {
                    updateParent(value);
                }
            }}
        >
            {({
                getInputProps,
                getItemProps,
                getLabelProps,
                getMenuProps,
                isOpen,
                inputValue,
                highlightedIndex,
                getRootProps,
                closeMenu,
                clearSelection,
                setState,
                selectedItem,
            }) => (
                <Wrapper isModal={isModal && isOpen && inputValue !== ""}>
                    {srOnly ? (
                        <SrOnlyLabel htmlFor={id} {...getLabelProps()}>
                            {label}
                        </SrOnlyLabel>
                    ) : (
                        <LabelWrapper>
                            <Label htmlFor={id} {...getLabelProps()}>
                                {label}
                            </Label>
                        </LabelWrapper>
                    )}
                    <div {...getRootProps({}, { suppressRefError: true })}>
                        <AutocompleteInput
                            {...{
                                id,
                                closeMenu,
                                inputValue,
                                placeholder,
                                getInputProps,
                                isModal,
                                resetParentState,
                                setState,
                                queryParam: query,
                                clearInput: () => {
                                    clearSelection();
                                    dispatch({ type: "CLEAR_STATE" });
                                },
                                updateParent: (value) => {
                                    updateParent({ title: value });
                                    setState({
                                        selectedItem: { title: value },
                                    });
                                    closeModal();
                                },
                            }}
                        />
                        <AutocompleteResults
                            {...{
                                getMenuProps,
                                getItemProps,
                                isOpen,
                                isModal,
                                highlightedIndex,
                                inputValue,
                                selectedItem,
                                suggestions: state.suggestions,
                                loading: state.loading,
                                noMatches: state.noMatches,
                            }}
                        />
                    </div>
                </Wrapper>
            )}
        </Downshift>
    );
};

Autocomplete.propTypes = {
    updateParent: PropTypes.func,
    paramName: PropTypes.string,
    srOnly: PropTypes.bool,
    isDocumentLibrary: PropTypes.bool,
    isModal: PropTypes.bool,
    placeholder: PropTypes.string,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    closeModal: PropTypes.func,
    resetParentState: PropTypes.func,
};

export default Autocomplete;
